/**
 * Analytics API Module - Dashboard and Chart Data
 * Extracted from monolithic silo-api.ts for modular architecture
 */

import { type RequestFunction } from './types'
import type {
  AdminDashboardStats,
  Contact,
  Order,
  InventoryItem,
  User,
  PaginatedResponse,
  FilterParams,
} from './types'

export class AnalyticsApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // DASHBOARD STATS
  // ============================================================================

  async getDashboardStats(): Promise<AdminDashboardStats> {
    try {
      // Use new dedicated dashboard stats endpoint
      return this.request('/api/analytics/dashboard-stats/')
    } catch (error: any) {
      if (error.message.includes('403') || error.message.includes('Forbidden')) {
        console.warn('Dashboard stats endpoint requires admin permissions:', error)
        throw new Error('Admin permission required to view dashboard stats')
      }
      throw error
    }
  }

  /**
   * Get dashboard summary statistics (alias for dashboard-stats)
   * This appears to be a duplicate endpoint in the backend
   */
  async getDashboardSummary(): Promise<AdminDashboardStats> {
    return this.request('/api/analytics/dashboard-summary/')
  }

  async getDashboardInventoryAlerts(): Promise<{
    low_stock: InventoryItem[]
    out_of_stock: InventoryItem[]
    overstocked: InventoryItem[]
  }> {
    return this.request('/api/dashboard/inventory-alerts/')
  }

  // ============================================================================
  // ORDER ANALYTICS
  // ============================================================================

  async getOrderStats(period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<{
    total_orders: number
    total_revenue: number
    average_order_value: number
    by_status: Record<string, number>
  }> {
    return this.request('/api/dashboard/order-stats/', { params: { period } })
  }

  async getOrderStatsAdmin(dateFrom?: string, dateTo?: string): Promise<{
    total_orders: number
    total_revenue: number
    orders_by_status: Record<string, number>
    revenue_by_day: Array<{ date: string; revenue: number }>
  }> {
    return this.request('/api/dashboard/order-stats/', { 
      params: { 
        date_from: dateFrom, 
        date_to: dateTo 
      }
    })
  }

  async getOrderStatusDistribution(): Promise<{
    status_counts: Record<string, number>
    status_values: Record<string, number>
    total_orders: number
    total_value: number
  }> {
    return this.request('/api/analytics/order-status-distribution/')
  }

  // ============================================================================
  // REVENUE ANALYTICS
  // ============================================================================

  async getRevenueAnalytics(params?: { 
    period?: 'daily' | 'weekly' | 'monthly'
    start_date?: string
    end_date?: string 
  }): Promise<{
    labels: string[]
    revenue_data: number[]
    order_count_data: number[]
    total_revenue: number
    total_orders: number
    period: string
  }> {
    const searchParams = {
      period: 'monthly',
      start_date: '2025-01-01',
      end_date: '2025-12-31',
      ...params
    }
    return this.request('/api/analytics/revenue/', { params: searchParams })
  }

  // ============================================================================
  // CUSTOMER ANALYTICS
  // ============================================================================

  async getTopCustomers(limit: number = 10): Promise<Array<{
    contact: Contact
    total_orders: number
    total_spent: number
  }>> {
    return this.request('/api/dashboard/top-customers/', { params: { limit } })
  }

  // ============================================================================
  // RECENT DATA
  // ============================================================================

  async getRecentOrders(limit: number = 10): Promise<Order[]> {
    return this.request('/api/dashboard/recent-orders/', { params: { limit } })
  }

  async getRecentOrdersAdmin(limit: number = 10): Promise<Order[]> {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    
    const params: FilterParams = {
      page: 1,
      limit,
      date_from: thirtyDaysAgo.toISOString().split('T')[0],
      ordering: '-timestamp'
    }
    
    const response = await this.request<PaginatedResponse<Order>>('/api/ar/orders/', { params })
    return response.results
  }
}